import { useEffect, useRef, useState } from 'react';

/**
 * Reusable scroll-driven "story" engine — the mechanics behind the homepage "how it works"
 * walk-through and each feature page's step-by-step. It owns ONLY the choreography; the caller
 * supplies the beat copy and a stage renderer, so every story can draw a different graphic while
 * sharing identical, tuned scroll behaviour.
 *
 * Layout (one model, repositioned per breakpoint):
 *   - md+ : the stage is pinned in the RIGHT column beside the scrolling step text.
 *   - <md : the same stage is pinned to the TOP (just under the sticky header) and the step text
 *           scrolls up and fades under it.
 *
 * Choreography: an IntersectionObserver picks the active beat (its band is biased low on mobile,
 * where the steps sit under the stage); a scroll listener yields a 0→1 progress `p` for drawing
 * effects. Under prefers-reduced-motion `p` is pinned to 1 (graphics show "complete") — every
 * beat's copy is always in the DOM, so no-JS and crawlers get the full story.
 */

export interface StoryBeat {
  /** 1-based beat index; also the value the stage renderer switches on. */
  n: number;
  /** Short mono label, e.g. "Set up" / "Cluster". */
  kicker: string;
  title: string;
  body: React.ReactNode;
  /** Optional mono chips under the copy. */
  facts?: string[];
}

export function ScrollStory({
  beats,
  renderStage,
  className = 'mt-4',
}: {
  beats: StoryBeat[];
  /** Returns the stage graphic for the current beat + scroll progress. */
  renderStage: (beat: number, p: number) => React.ReactNode;
  className?: string;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<Array<HTMLElement | null>>([]);
  const [beat, setBeat] = useState(1);
  // Both of these start in the COMPLETE state, and the effect below downgrades them to the
  // scroll-driven one. That direction is deliberate: prerendered HTML, no-JS visitors and the
  // window before hydration all render "every step lit, wire fully drawn" — a clean static story —
  // instead of the old default, which left five of six steps at 40% opacity and the connector
  // undrawn, i.e. looking deliberately de-emphasised rather than simply un-animated.
  //
  // `live` is false on the prerender AND on the first client render, so hydration matches; the
  // effect is what flips it. Correcting `p` at that moment is a visible change, which is why
  // <DeferredHydration> hydrates this subtree well before the section reaches the viewport.
  const [p, setP] = useState(1);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    const steps = stepRefs.current.filter(Boolean) as HTMLElement[];
    if (!section || steps.length === 0 || typeof window === 'undefined') return;
    const reduce = 'matchMedia' in window && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    // On mobile the steps live UNDER the pinned stage, so the active band sits low in the
    // viewport; on desktop the steps have their own column, so it's centred.
    const rootMargin = mobile ? '-66% 0px -26% 0px' : '-48% 0px -48% 0px';

    let io: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              const n = Number((e.target as HTMLElement).dataset.beat);
              if (n) setBeat(n);
            }
          }
        },
        { rootMargin, threshold: 0 },
      );
      steps.forEach((s) => io!.observe(s));
    }

    const onScroll = () => {
      if (reduce) {
        setP(1);
        return;
      }
      const r = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = r.height - vh;
      const prog = total > 0 ? Math.min(1, Math.max(0, (vh * 0.5 - r.top) / total)) : 1;
      setP(prog);
    };
    onScroll();
    // Batched with the setP above into one render, so the story never paints a half-applied
    // state (lit steps with a scroll-derived wire, or vice versa).
    setLive(true);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div ref={sectionRef} className={`${className} md:grid md:grid-cols-[0.86fr_1.14fr] md:gap-12`}>
      {/* Stage — first in DOM so it's ON TOP on mobile; moved to the right column on desktop. */}
      <div className="sticky top-[75px] z-30 -mx-6 mb-2 bg-bg px-6 pb-3 pt-3 md:top-0 md:z-auto md:order-2 md:mx-0 md:mb-0 md:flex md:h-screen md:items-center md:bg-transparent md:px-0 md:pb-0 md:pt-0">
        {renderStage(beat, p)}
      </div>

      {/* Steps (text). On mobile they flow under the pinned stage and scroll up behind it. */}
      <div className="md:order-1">
        {beats.map((b, i) => (
          <article
            key={b.n}
            ref={(el) => {
              stepRefs.current[i] = el;
            }}
            data-beat={b.n}
            className={`flex min-h-[58vh] flex-col justify-center py-2 transition-opacity duration-300 md:min-h-[68vh] ${
              !live || beat === b.n ? 'opacity-100' : 'opacity-40'
            }`}
          >
            <div className="font-mono text-[12px] tracking-[0.1em] text-accent">
              {String(b.n).padStart(2, '0')} · {b.kicker}
            </div>
            <h3 className="mt-2.5 font-display text-[24px] font-bold leading-tight tracking-tight md:mt-3 md:text-[32px]">
              {b.title}
            </h3>
            <p className="mt-3 max-w-[46ch] text-[15px] leading-relaxed text-muted md:text-[15.5px]">{b.body}</p>
            {b.facts && b.facts.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {b.facts.map((t) => (
                  <span
                    key={t}
                    className="rounded-lg border border-border bg-panel px-2.5 py-1.5 font-mono text-[11.5px] text-txt"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}

/* ── Shared stage building blocks (used by every story's stage renderer) ───────────────── */

/** The bordered stage canvas with the faint grid backdrop. Children are positioned inside it. */
export function StageCanvas({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative h-[336px] w-full overflow-hidden rounded-[18px] border border-border bg-panel shadow-[0_30px_80px_-40px_rgba(0,0,0,0.5)] md:h-[min(74vh,600px)] md:rounded-[22px]">
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          backgroundImage:
            'linear-gradient(var(--bg-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--bg-grid-line) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
        }}
      />
      {children}
    </div>
  );
}

/** A labelled card used inside stages. */
export function StageCard({ head, children, tone }: { head: string; children: React.ReactNode; tone?: string }) {
  return (
    <div className="rounded-lg border border-border bg-panel2/95 px-3 py-2.5 md:rounded-xl md:px-4 md:py-3">
      <div className={`mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] md:mb-2 md:text-[10.5px] ${tone ?? 'text-faint'}`}>
        {head}
      </div>
      {children}
    </div>
  );
}

/** Cross-fades a set of per-beat panels inside a stage: only the active beat's panel shows. */
export function StagePanels({ beat, panels }: { beat: number; panels: Array<{ n: number; content: React.ReactNode }> }) {
  return (
    <div className="absolute inset-x-3 bottom-3 top-[86px] flex flex-col justify-end md:inset-x-[26px] md:bottom-[26px] md:top-[104px]">
      {panels.map((pan) => (
        <div
          key={pan.n}
          aria-hidden={beat !== pan.n}
          className={`flex flex-col gap-2.5 transition-all duration-500 md:gap-3 ${
            beat === pan.n
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 opacity-0'
          }`}
        >
          {pan.content}
        </div>
      ))}
    </div>
  );
}
